import React, { useState, useRef, useEffect } from 'react';

export function AudioPlayer({ audioData }) {
    const [isPlaying, setIsPlaying] = useState(false);
    const [error, setError] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const audioRef = useRef(null);

    // Cleanup function for the audio URL
    useEffect(() => {
        return () => {
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
            }
        };
    }, [audioUrl]);

    // Process audio data when component mounts or audioData changes
    useEffect(() => {
        const processAudioData = async () => {
            setIsLoading(true);
            try {
                const blob = getAudioBlob();
                if (blob) {
                    const url = URL.createObjectURL(blob);
                    setAudioUrl(url);
                    setError(null);
                }
            } catch (error) {
                console.error('Error processing audio:', error);
                setError('Failed to process audio: ' + error.message);
            } finally {
                setIsLoading(false);
            }
        };

        if (audioData) {
            processAudioData();
        }
    }, [audioData]);

    const getAudioBlob = () => {
        try {
            if (!audioData) {
                throw new Error('No audio data provided');
            }

            // Debug logging
            console.log('Audio data received:', {
                type: audioData.type,
                dataLength: audioData.data ? audioData.data.length : 0
            });

            if (audioData.type === 'mp3' && audioData.data) {
                // Validate base64 string
                if (!/^[A-Za-z0-9+/=]+$/.test(audioData.data)) {
                    throw new Error('Invalid base64 data');
                }

                try {
                    // Convert base64 to blob
                    const byteCharacters = atob(audioData.data);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    return new Blob([byteArray], { type: 'audio/mp3' });
                } catch (e) {
                    console.error('Base64 decoding error:', e);
                    throw new Error('Failed to decode base64 data: ' + e.message);
                }
            } else {
                throw new Error(`Unsupported audio format: ${audioData.type || 'unknown'}`);
            }
        } catch (error) {
            console.error('Error processing audio data:', error);
            setError('Failed to process audio data: ' + error.message);
            return null;
        }
    };

    const handlePlay = async () => {
        if (!audioData) {
            setError('No audio data available');
            return;
        }

        try {
            setError(null);
            if (audioRef.current) {
                await audioRef.current.play();
                setIsPlaying(true);
            }
        } catch (error) {
            console.error('Error playing audio:', error);
            setError('Failed to play audio: ' + error.message);
            setIsPlaying(false);
        }
    };

    const handlePause = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            setIsPlaying(false);
        }
    };

    const handleEnded = () => {
        setIsPlaying(false);
    };

    const handleError = (e) => {
        const errorMessage = e.target.error
            ? `Error code: ${e.target.error.code}, message: ${e.target.error.message}`
            : 'Unknown error';
        console.error('Audio element error:', errorMessage);
        setError('Error playing audio: ' + errorMessage);
        setIsPlaying(false);
    };

    // Add canPlay handler to ensure audio is loaded
    const handleCanPlay = () => {
        console.log('Audio can be played');
        setIsLoading(false);
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-start space-y-2 mt-2">
                <div className="flex items-center space-x-2">
                    <div className="glass-morphism flex items-center justify-center w-10 h-10 rounded-full">
                        <div className="h-5 w-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                    <span className="text-sm text-gray-300">Generating audio...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-start space-y-2 mt-2">
            <audio
                ref={audioRef}
                src={audioUrl}
                onEnded={handleEnded}
                onError={handleError}
                onCanPlay={handleCanPlay}
                preload="auto"
                className="hidden"
            />
            <div className="flex items-center space-x-2">
                {isPlaying ? (
                    <button
                        onClick={handlePause}
                        className="glass-morphism flex items-center justify-center w-10 h-10 rounded-full hover:bg-emerald-700/30 transition-colors"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                    </button>
                ) : (
                    <button
                        onClick={handlePlay}
                        className="glass-morphism flex items-center justify-center w-10 h-10 rounded-full hover:bg-emerald-700/30 transition-colors"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                        </svg>
                    </button>
                )}
                <span className="text-sm text-gray-300">
                    {isPlaying ? 'Playing audio...' : 'Click to play audio'}
                </span>
            </div>
            {error && (
                <div className="text-sm text-red-400 whitespace-pre-wrap break-words max-w-full">
                    {error}
                </div>
            )}
        </div>
    );
} 